import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Calculator, 
  Clock, 
  ArrowUpRight, 
  ShieldCheck, 
  Sparkles,
  Layers,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getCurrentTerm, Term, getApplicableTermFees } from "@/utils/feeCalculations";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface ClassData {
  name: string;
  classUid: string;
  nextClassUid?: string;
  level?: string;
}

interface ProjectedFeePaymentsViewProps {
  onBack: () => void;
  classes: ClassData[];
  levelFees: any[];
  terms: Term[];
  schoolType: string;
  schoolID: string;
  studentType?: string;
  admissionGrade?: string;
  selectedClass: string;
  admissionDate: string;
  isOngoing: boolean;
  outstandingBalance?: number;
  feeAssessmentConfig?: any;
  embedded?: boolean;
}

const ProjectedFeePaymentsView: React.FC<ProjectedFeePaymentsViewProps> = ({
  onBack,
  classes,
  levelFees,
  terms,
  schoolType,
  studentType,
  admissionGrade,
  selectedClass,
  admissionDate,
  isOngoing,
  outstandingBalance = 0,
  feeAssessmentConfig,
  embedded = false,
}) => {
  const [projections, setProjections] = useState<any[]>([]);
  const [totalProjectedCost, setTotalProjectedCost] = useState(0);

  useEffect(() => {
    if (!selectedClass || classes.length === 0 || levelFees.length === 0 || terms.length === 0) return;

    const isDay = studentType === "Day School" || schoolType === "Day School";
    let currentClassObj = classes.find((c: ClassData) => c.name === selectedClass);
    if (!currentClassObj) return;

    let startYear = new Date(admissionDate).getFullYear();
    if (isOngoing) {
      // For ongoing students, the simulation of their CURRENT class starts in the CURRENT year
      startYear = new Date().getFullYear();
    }
    
    let currentYear = startYear;
    const admTerm = getCurrentTerm(terms, new Date(admissionDate));
    let startingTermNum = admTerm ? Number(admTerm.number) : 1;

    const projectedSequence: any[] = [];
    let cumulativeCost = 0;
    
    if (isOngoing && outstandingBalance > 0) {
      cumulativeCost += outstandingBalance;
      projectedSequence.push({
        year: startYear - 1,
        className: "Opening Balance",
        isHistorical: true,
        terms: [],
        annualTotal: outstandingBalance,
      });
    } else if (isOngoing && outstandingBalance < 0) {
      cumulativeCost += outstandingBalance;
      projectedSequence.push({
        year: startYear - 1,
        className: "Opening Credit",
        isHistorical: true,
        isCredit: true,
        terms: [],
        annualTotal: outstandingBalance,
      });
    }
    let loopCount = 0;
    let iterClassObj: ClassData | undefined = currentClassObj;
    let iterTermNum = startingTermNum;
    const dateSimulation = new Date(admissionDate);
    
    const getLevelFee = (cls: ClassData) => {
      let uniformMatch = levelFees.find((l: any) => l.feeType === 'uniform' || l.feeType === 'Uniform');
      if (uniformMatch) return uniformMatch;

      let matched = levelFees.find((l: any) =>
        l.classes && l.classes.some((c: string) => 
            c.toLowerCase() === cls.name.toLowerCase() || 
            cls.name.toLowerCase().includes(c.toLowerCase()) ||
            c.toLowerCase().includes(cls.name.toLowerCase())
        )
      );
      
      if (!matched && cls.level && typeof cls.level === 'string') {
        matched = levelFees.find((l: any) => 
            l.level && typeof l.level === 'string' && 
            (l.level.toLowerCase() === cls.level!.toLowerCase() || 
             cls.level!.toLowerCase().includes(l.level.toLowerCase()) ||
             l.level.toLowerCase().includes(cls.level!.toLowerCase()))
        );
      }

      if (!matched) {
        matched = levelFees.find((l: any) => 
            l.level && typeof l.level === 'string' &&
            (cls.name.toLowerCase().includes(l.level.toLowerCase()) || 
             l.level.toLowerCase().includes(cls.name.toLowerCase()))
        );
      }

      if (!matched && admissionGrade) {
        matched = levelFees.find((l: any) => 
            l.level && typeof l.level === 'string' && 
            (l.level.toLowerCase() === admissionGrade.toLowerCase() ||
             admissionGrade.toLowerCase().includes(l.level.toLowerCase()))
        );
      }

      return matched;
    };

    while (iterClassObj && iterClassObj.classUid !== "ALUMNI" && loopCount < 15) {
      const clsFeeConfig = getLevelFee(iterClassObj);
      
      const yearProjection = {
        year: currentYear,
        className: iterClassObj.name,
        isHistorical: false,
        terms: [] as any[],
        annualTotal: 0,
        feeConfig: clsFeeConfig,
      };

      if (!clsFeeConfig) {
        yearProjection.terms.push(
          { termNum: 1, amount: 0, skipped: iterTermNum > 1 },
          { termNum: 2, amount: 0, skipped: iterTermNum > 2 },
          { termNum: 3, amount: 0, skipped: false }
        );
      } else {
        const isFirstYearOfSimulation = loopCount === 0;
        let applicableObj;
         if (isFirstYearOfSimulation && !isOngoing) {
            applicableObj = getApplicableTermFees(dateSimulation, clsFeeConfig, terms, false, isDay, feeAssessmentConfig);
         } else {
            const dummyDate = new Date(currentYear, 0, 15);
            applicableObj = getApplicableTermFees(dummyDate, clsFeeConfig, terms, true, isDay, feeAssessmentConfig);
         }

        let t1 = (isFirstYearOfSimulation && !isOngoing && iterTermNum > 1) ? 0 : (isFirstYearOfSimulation && isOngoing && iterTermNum > 1 ? 0 : applicableObj.term1);
        let t2 = (isFirstYearOfSimulation && !isOngoing && iterTermNum > 2) ? 0 : (isFirstYearOfSimulation && isOngoing && iterTermNum > 2 ? 0 : applicableObj.term2);
        let t3 = applicableObj.term3;

        yearProjection.terms.push(
          { termNum: 1, amount: t1, skipped: isFirstYearOfSimulation && iterTermNum > 1 },
          { termNum: 2, amount: t2, skipped: isFirstYearOfSimulation && iterTermNum > 2 },
          { termNum: 3, amount: t3, skipped: false }
        );

        yearProjection.annualTotal = t1 + t2 + t3;
      }
      
      cumulativeCost += yearProjection.annualTotal;
      projectedSequence.push(yearProjection);

      if (iterClassObj.nextClassUid && iterClassObj.nextClassUid !== 'ALUMNI') {
        iterClassObj = classes.find((c: ClassData) => c.classUid === iterClassObj!.nextClassUid);
      } else {
        iterClassObj = undefined;
      }

      currentYear++;
      iterTermNum = 1;
      loopCount++;
    }

    setProjections(projectedSequence);
    setTotalProjectedCost(Math.max(0, cumulativeCost));
  }, [selectedClass, classes, levelFees, terms, studentType, admissionDate, isOngoing, outstandingBalance, feeAssessmentConfig]);

  const entryProjection = projections.find((p: any) => !p.isHistorical && p.feeConfig);
  const entryTerm = entryProjection ? entryProjection.terms.find((t: any) => !t.skipped) : null;

  return (
    <div className={`w-full ${embedded ? 'p-0' : 'bg-white dark:bg-slate-950 p-4 md:p-8'} text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300`}>
      {/* Professional Dashboard Nav */}
      {!embedded && (
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBack} 
              className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Back</span>
            </Button>
            <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-800" />
            <div className="min-w-0">
              <h1 className="text-base font-bold text-slate-900 dark:text-white leading-none truncate">
                Fee Projection Analysis
              </h1>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-1 truncate">
                Registry • {selectedClass}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Metrics Segment */}
        <div className="lg:col-span-4 space-y-4">
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden rounded-xl">
            <CardContent className="p-6">
              <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-4">Total Projected (KES)</span>
              <h2 className="text-3xl font-bold tracking-tighter text-slate-900 dark:text-white mb-6">
                {Math.round(totalProjectedCost).toLocaleString()}
              </h2>
              
              <div className="space-y-3 pt-6 border-t border-slate-50 dark:border-slate-800">
                <div className="flex justify-between items-center text-left">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Modality</span>
                  <span className="text-[10px] font-black text-slate-700 dark:text-slate-300 uppercase bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                    {schoolType === "Mixed (Day & Boarding)" ? studentType : schoolType}
                  </span>
                </div>
                <div className="flex justify-between items-center text-left">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Duration</span>
                  <span className="text-[11px] font-bold text-slate-800 dark:text-white uppercase">
                    {projections.filter((p: any) => !p.isHistorical).length} Cycles
                  </span>
                </div>
                <div className="flex justify-between items-center text-left">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Finish Year</span>
                  <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase">
                    {projections.length > 0 ? projections[projections.length - 1].year : 'N/A'}
                  </span>
                </div>
              </div>

              {entryProjection && entryTerm && (
                <div className="mt-8">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full text-[10px] font-bold uppercase tracking-widest h-10 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 focus:ring-0">
                        View Itemization <ArrowUpRight className="ml-2 h-3 w-3" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg p-0 bg-white dark:bg-slate-950 rounded-2xl border-slate-200 dark:border-slate-800 overflow-hidden shadow-2xl">
                      <div className="bg-slate-900 p-6 text-white text-left">
                        <span className="text-[9px] font-bold tracking-[0.2em] text-slate-500 uppercase block mb-1">Detailed Simulation</span>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-bold text-white uppercase italic">{entryProjection.className}</DialogTitle>
                        </DialogHeader>
                        <div className="mt-6 flex justify-between items-end border-t border-white/10 pt-4">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Phase Total</span>
                          <span className="text-2xl font-bold">KES {entryTerm.skipped ? 0 : Math.round(entryTerm.amount).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                          <Table>
                            <TableBody>
                              {entryProjection.feeConfig?.categories.map((cat: any, i: number) => {
                                  const isDay = studentType === "Day School" || schoolType === "Day School";
                                  const val = isDay ? (entryTerm.termNum === 1 ? cat.daySchoolTerm1 : entryTerm.termNum === 2 ? cat.daySchoolTerm2 : cat.daySchoolTerm3) ?? (entryTerm.termNum === 1 ? cat.term1 : entryTerm.termNum === 2 ? cat.term2 : cat.term3) ?? 0 : (entryTerm.termNum === 1 ? cat.term1 : entryTerm.termNum === 2 ? cat.term2 : cat.term3) ?? 0;
                                  
                                  let statusLabel = "Recurrent Stage";
                                  let isExempt = false;
                                  if (cat.paidOnce) {
                                    if (isOngoing) { isExempt = true; statusLabel = "Admission Waived"; }
                                    else { statusLabel = "One-time Charge"; }
                                  }
                                  if (!isExempt && !isOngoing && feeAssessmentConfig) {
                                    const startTermObj = getCurrentTerm(terms, new Date(admissionDate));
                                    if (startTermObj && Number(startTermObj.number) === entryTerm.termNum) {
                                      statusLabel = "Prorated Cycle";
                                    }
                                  }

                                  return (
                                    <TableRow key={i} className="hover:bg-slate-50 dark:hover:bg-slate-900 border-b border-slate-100 dark:border-slate-800/50">
                                      <TableCell className="py-4 text-left">
                                        <div className="flex flex-col">
                                          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider leading-none mb-1">{cat.name}</span>
                                          <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{statusLabel}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right py-4">
                                        {isExempt ? (
                                          <span className="text-[10px] font-bold text-slate-300 dark:text-slate-600 uppercase tracking-widest italic line-through">Waived</span>
                                        ) : (
                                          <span className="text-[11px] font-bold text-slate-900 dark:text-white">KES {Math.round(val).toLocaleString()}</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                        {entryProjection.feeConfig?.isGovernmentSponsored && entryProjection.feeConfig?.governmentSponsorshipAmount > 0 && (
                          <div className="mt-6 p-4 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest italic flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4" /> Government Credit Applied
                              </span>
                              <Badge className="bg-blue-600 text-white border-0 text-[10px] font-black tracking-widest px-2 py-0.5 rounded">ACTIVE</Badge>
                            </div>
                            <div className="flex justify-between items-center text-[10px] font-bold bg-white/40 dark:bg-black/20 p-2 rounded-lg border border-blue-200/50 dark:border-blue-900/30">
                              <span className="text-blue-500 uppercase">Deductible Amount</span>
                              <span className="text-blue-700 dark:text-blue-300">KES {Math.round(entryProjection.feeConfig.governmentSponsorshipAmount).toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200/50 dark:border-slate-800/50">
            <div className="flex items-center gap-2 mb-2 text-slate-400">
               <Activity className="h-3 w-3" />
               <span className="text-[9px] font-bold uppercase tracking-widest">Projection Logic</span>
            </div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed italic">
              Estimated expenditures are modeled on currently deployed fee structures.
            </p>
          </div>
        </div>

        {/* Right Column: Interactive List */}
        <div className="lg:col-span-8 flex flex-col min-w-0">
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-xl overflow-hidden min-w-0">
            <CardHeader className="py-4 px-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20 flex flex-row items-center justify-between">
               <div className="flex items-center gap-2">
                 <Layers className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                 <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Progression Path</span>
               </div>
               <Badge className="bg-white dark:bg-slate-950 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-800 text-[9px] font-bold">
                 {projections.filter((p: any) => !p.isHistorical).length} NODES
               </Badge>
            </CardHeader>
            <CardContent className="p-0 flex-1 min-w-0">
              <div className="divide-y divide-slate-100 dark:divide-slate-800/50 min-w-0">
                {projections.map((proj: any, idx: number) => (
                  <div key={idx} className={`p-4 transition-all hover:bg-slate-50 dark:hover:bg-slate-800/30 group min-w-0 ${proj.isHistorical ? 'bg-slate-50/30 dark:bg-slate-950/10' : ''}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 min-w-0">
                      {/* Class Identification */}
                      <div className="flex items-center gap-4 min-w-0 flex-1 text-left">
                        <div className={`h-8 w-8 min-w-[32px] rounded-lg flex items-center justify-center text-[10px] font-black shadow-sm ${
                          proj.isHistorical ? 'bg-slate-100 dark:bg-slate-800 text-slate-500' : 'bg-slate-900 dark:bg-white text-white dark:text-black'
                        }`}>
                          {proj.year === 'Prev' ? 'REF' : String(proj.year).slice(-2)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className={`text-sm font-bold truncate transition-colors duration-200 ${
                            proj.isHistorical ? 'text-slate-400 italic' : 'text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                          }`}>
                            {proj.className}
                          </h3>
                        </div>
                      </div>

                      {/* Term Data & Totals */}
                      <div className="flex items-center gap-x-8 gap-y-2 flex-wrap md:flex-nowrap min-w-0">
                        {!proj.isHistorical ? (
                          <div className="flex gap-x-6 min-w-0">
                            {proj.terms.map((t: any) => (
                              <div key={t.termNum} className={`flex flex-col min-w-[45px] transition-opacity duration-200 ${t.skipped ? 'opacity-20' : 'opacity-100'}`}>
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T0{t.termNum}</span>
                                <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">{t.skipped ? '—' : Math.round(t.amount).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[9px] font-bold text-slate-300 dark:text-slate-700 uppercase italic tracking-widest">Entry Balance</span>
                        )}
                        <div className="md:pl-6 md:border-l border-slate-100 dark:border-slate-800 min-w-[100px] text-left md:text-right">
                          <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-0.5">Phase Total</span>
                          <span className={`text-sm font-bold tracking-tight transition-colors duration-200 ${
                            proj.isCredit ? 'text-blue-600' : 'text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400'
                          }`}>
                            {proj.isCredit ? '-' : ''}{Math.round(Math.abs(proj.annualTotal)).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Status Indicator */}
                {projections.length > 0 && !projections[projections.length-1].isHistorical && (
                  <div className="p-6 flex flex-col items-center bg-slate-50/10 dark:bg-slate-950/10 border-t border-dashed border-slate-200 dark:border-slate-800">
                     <div className="flex items-center gap-2">
                        <Sparkles className="h-3 w-3 text-blue-500 opacity-50" />
                        <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.4em]">Simulation Node End</p>
                     </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProjectedFeePaymentsView;
